const MARKDOWN_SOURCE_LINK_REGEX = /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g;
const MARKDOWN_SOURCE_LINK_FULL_REGEX = /^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/;
const LEGAL_REFERENCE_PATTERN = 'art\\.\\s*\\d+[A-Za-z0-9]*(?:\\.\\d+[A-Za-z0-9]*)?';
const STANDALONE_LEGAL_REFERENCE_PATTERN = '(?:arts?\\.|articulos?|artículos?)\\s*\\d+[A-Za-z0-9]*(?:\\.\\d+[A-Za-z0-9]*)?(?:\\s*(?:,|y|e|/)\\s*\\d+[A-Za-z0-9]*(?:\\.\\d+[A-Za-z0-9]*)?)*';
const SOURCES_HEADING_REGEX = /^#{1,6}\s*fuentes\s+consultadas\s*$/i;
const GENERIC_SOURCE_LABEL_REGEX = /^(fuente|source|link|enlace|web|referencia|\d+)$/i;
const COMPLETE_NATIVE_CITATION_REGEX = /^(?:\(\s*)?(?:\[[^\]]+\]\(https?:\/\/[^)\s]+\)|【\d+†[^】]+】|cite[^]+)(?:\s*\))?$/u;

export const simplifyUrlForClipboard = (url) => {
  try {
    const parsedUrl = new URL(url);
    Array.from(parsedUrl.searchParams.keys()).forEach((key) => {
      if (key.toLowerCase().startsWith('utm_')) {
        parsedUrl.searchParams.delete(key);
      }
    });
    return parsedUrl.toString();
  } catch {
    return url;
  }
};

const normalizeUrlForComparison = (url) => simplifyUrlForClipboard(url).replace(/\/$/, '');

export const getDomainSourceLabel = (url) => {
  try {
    const { hostname } = new URL(url);
    return hostname
      .replace(/^www\./i, '')
      .split('.')
      .slice(-2)
      .join('.');
  } catch {
    return '';
  }
};

export const getStructuredCitationForUrl = (url, sourceCitations = []) => {
  const canonicalUrl = normalizeUrlForComparison(url);
  return sourceCitations.find((citation) => (
    citation?.url && normalizeUrlForComparison(citation.url) === canonicalUrl
  ));
};

export const getUrlSourceLabel = (url, label) => {
  const lowerUrl = url.toLowerCase();
  const cleanLabel = label.trim();

  if (lowerUrl.includes('boe.es')) return 'BOE';
  if (lowerUrl.includes('fiscal.es')) return 'Fiscalia';
  if (lowerUrl.includes('tribunalconstitucional.es')) return 'TC';
  if (lowerUrl.includes('dgt.es')) return 'DGT';
  if (lowerUrl.includes('interior.gob.es')) return 'Interior';
  if (lowerUrl.includes('policia.es')) return 'Policia Nacional';
  if (lowerUrl.includes('guardiacivil.es')) return 'Guardia Civil';
  if (lowerUrl.includes('poderjudicial.es') || lowerUrl.includes('cgpj.es')) {
    return lowerUrl.includes('cendoj') ? 'CENDOJ' : 'CGPJ';
  }

  if (cleanLabel && !GENERIC_SOURCE_LABEL_REGEX.test(cleanLabel) && cleanLabel.length <= 28) {
    return cleanLabel;
  }

  return getDomainSourceLabel(url) || cleanLabel || 'Fuente';
};

export const getSourceLabelForUrl = (url, label = '', sourceCitations = []) => {
  const structuredCitation = getStructuredCitationForUrl(url, sourceCitations);
  return getUrlSourceLabel(url, structuredCitation?.title || label);
};

export const applyNativeSourceCitations = (text, sourceCitations = []) => {
  if (!text || typeof text !== 'string' || !Array.isArray(sourceCitations)) return text;

  const positioned = sourceCitations
    .filter((citation) => (
      citation
      && typeof citation.url === 'string'
      && /^https?:\/\//i.test(citation.url)
      && Number.isInteger(citation.startIndex)
      && Number.isInteger(citation.endIndex)
      && citation.startIndex >= 0
      && citation.endIndex > citation.startIndex
      && citation.endIndex <= text.length
      && COMPLETE_NATIVE_CITATION_REGEX.test(text.slice(citation.startIndex, citation.endIndex).trim())
    ))
    .sort((left, right) => right.startIndex - left.startIndex || right.endIndex - left.endIndex);

  const groups = positioned.reduce((result, citation) => {
    const key = `${citation.startIndex}:${citation.endIndex}`;
    const group = result.get(key) || {
      startIndex: citation.startIndex,
      endIndex: citation.endIndex,
      citations: []
    };
    if (!group.citations.some((item) => normalizeUrlForComparison(item.url) === normalizeUrlForComparison(citation.url))) {
      group.citations.push(citation);
    }
    result.set(key, group);
    return result;
  }, new Map());

  let result = text;
  let earliestAppliedIndex = text.length;

  Array.from(groups.values()).forEach((group) => {
    if (group.endIndex > earliestAppliedIndex) return;

    const citedText = text.slice(group.startIndex, group.endIndex);
    const chips = group.citations.map((citation) => {
      const label = getUrlSourceLabel(citation.url, citation.title || citedText);
      return `[${label}](${simplifyUrlForClipboard(citation.url)})`;
    }).join(' ');
    result = `${result.slice(0, group.startIndex)}${chips}${result.slice(group.endIndex)}`;
    earliestAppliedIndex = group.startIndex;
  });

  return result;
};

const canonicalizeMarkdownSourceUrls = (text) => text.replace(
  MARKDOWN_SOURCE_LINK_REGEX,
  (_match, label, url) => `[${label}](${simplifyUrlForClipboard(url)})`
);

const removeTrackingResidueAfterMarkdownLink = (text) => text.replace(
  /(\[[^\]]+\]\(https?:\/\/[^)\s]+\))\s+(?:\d+)?(?:&utm_[^=\s)]+=[^&\s)]*)+(?:\)+)?/gi,
  '$1'
);

export const stripInlineMarkdown = (text) => text
  .replace(/\*\*([^*]+)\*\*/g, '$1')
  .replace(/\*([^*]+)\*/g, '$1')
  .replace(/__([^_]+)__/g, '$1')
  .replace(/_([^_]+)_/g, '$1')
  .replace(/`([^`]+)`/g, '$1')
  .trim();

export const cleanTechnicalArtifacts = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text.replace(/(?:cite)?turn\d+search\d+/g, '');
};

export const removeConsultedSourcesSection = (text) => {
  const lines = text.split('\n');
  const sourceHeadingIndex = lines.findIndex((line) => SOURCES_HEADING_REGEX.test(line.trim()));

  if (sourceHeadingIndex === -1) {
    return text;
  }

  return lines.slice(0, sourceHeadingIndex).join('\n').trimEnd();
};

const dedupeAdjacentSourceLinks = (text) => {
  let result = '';
  let lastIndex = 0;
  let previousCanonicalUrl = null;
  let textSincePreviousCitation = '';

  for (const match of text.matchAll(MARKDOWN_SOURCE_LINK_REGEX)) {
    const matchIndex = match.index ?? 0;
    const [rawLink, , url] = match;
    const between = text.slice(lastIndex, matchIndex);
    const canonicalUrl = normalizeUrlForComparison(url);
    const isDuplicate = canonicalUrl === previousCanonicalUrl
      && /^[\s.,;:()]*$/.test(textSincePreviousCitation + between);

    if (!isDuplicate) {
      result += between + rawLink;
      previousCanonicalUrl = canonicalUrl;
      textSincePreviousCitation = '';
    } else {
      result += between;
    }

    lastIndex = matchIndex + rawLink.length;
  }

  result += text.slice(lastIndex);
  return result;
};

const moveCitationAfterLegalReference = (text) => {
  const linkPattern = '\\[([^\\]]+)\\]\\((https?:\\/\\/[^)\\s]+)\\)';
  const linkThenReference = new RegExp(
    `\\(?\\s*(${linkPattern})\\s*[,;:]?\\s*(${LEGAL_REFERENCE_PATTERN})\\s*\\)?`,
    'gi'
  );

  return text.replace(linkThenReference, (_match, rawLink, _label, _url, reference) => `${reference} ${rawLink}`);
};

const moveTrailingPunctuationBeforeCitation = (text) => {
  const linkPattern = '(\\[[^\\]]+\\]\\(https?:\\/\\/[^)\\s]+\\)|\\[(?:Doctrina operativa|Base doctrina)\\])';
  const punctuationAfterCitation = new RegExp(`\\s+${linkPattern}\\s*([.,;:])`, 'gi');

  return text.replace(punctuationAfterCitation, (_match, rawLink, punctuation) => `${punctuation} ${rawLink}`);
};

const removeStandaloneLegalReferenceBeforeCitation = (text) => {
  const linkPattern = '(\\[[^\\]]+\\]\\(https?:\\/\\/[^)\\s]+\\))';
  const standaloneReferenceBeforeCitation = new RegExp(
    `(^|[.!?;:]\\s+|\\n\\s*)${STANDALONE_LEGAL_REFERENCE_PATTERN}\\s+${linkPattern}`,
    'gi'
  );

  return text.replace(standaloneReferenceBeforeCitation, (_match, prefix, rawLink) => `${prefix}${rawLink}`);
};

const removeOrphanSourceMarkerBeforeEquivalentLink = (text) => {
  const markerBeforeEquivalentLink = /\[([A-Za-zÁÉÍÓÚÜÑáéíóúüñ][A-Za-zÁÉÍÓÚÜÑáéíóúüñ0-9 ._-]{0,28})\]\s+(\[([^\]]+)\]\(https?:\/\/[^)\s]+\))/g;

  return text.replace(markerBeforeEquivalentLink, (match, markerLabel, rawLink, linkLabel) => (
    stripInlineMarkdown(markerLabel).toLowerCase() === stripInlineMarkdown(linkLabel).toLowerCase()
      ? rawLink
      : match
  ));
};

export const normalizeCitationMarkdownForRender = (text) => {
  if (!text || typeof text !== 'string') return text;

  return dedupeAdjacentSourceLinks(
    removeStandaloneLegalReferenceBeforeCitation(
      moveTrailingPunctuationBeforeCitation(
        removeOrphanSourceMarkerBeforeEquivalentLink(
          moveCitationAfterLegalReference(
            removeTrackingResidueAfterMarkdownLink(canonicalizeMarkdownSourceUrls(text))
          )
        )
        .replace(/\(\s*(\[[^\]]+\]\(https?:\/\/[^)\s]+\))\s*\)/g, '$1')
        .replace(/(\[[^\]]+\]\(https?:\/\/[^)\s]+\))(?=\S)/g, '$1 ')
        .replace(/(\S)(\[[^\]]+\]\(https?:\/\/[^)\s]+\))/g, '$1 $2')
      )
    )
  )
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .trim();
};

export const formatBotTextForClipboard = (text) => {
  return normalizeCitationMarkdownForRender(
    removeConsultedSourcesSection(cleanTechnicalArtifacts(text))
  )
    .replace(/\(?\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)\)?/g, (_match, label, url) => {
      const cleanLabel = stripInlineMarkdown(label);
      const sourceLabel = getUrlSourceLabel(url, cleanLabel);
      const cleanUrl = simplifyUrlForClipboard(url);
      return `${cleanLabel || sourceLabel} (${sourceLabel}): ${cleanUrl}`;
    })
    .replace(/\[(?:Doctrina operativa|Base doctrina)\]/gi, '(Doctrina operativa)')
    .split('\n')
    .map((line) => line.replace(/^#{1,6}\s*/, ''))
    .join('\n')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

export const isMarkdownSourceLink = (value) => MARKDOWN_SOURCE_LINK_FULL_REGEX.test(value.trim());
